import { useEffect, useRef, useState } from 'react'
import { ApiError } from '../api/client'
import { ErrorNotice } from '../components'

export interface UploadedImage { url: string; width: number; height: number }

export const plainToHtml = (text: string | null | undefined): string =>
  text ? `<p>${text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/\n/g, '<br>')}</p>` : ''

/** True when the HTML shows the reader something: words, or at least one image. */
export const hasRichContent = (html: string): boolean => /<img\b/i.test(html) || html.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim() !== ''

interface Props {
  label: string
  value: string
  onChange: (html: string) => void
  upload: (file: File) => Promise<UploadedImage>
  /** A one-line editor for options: no lists, and a short box. */
  compact?: boolean
}

/**
 * A small visual editor for question and option text: bold, italic, underline, sub and superscript, lists, and pictures. Pictures are uploaded
 * (the server resizes them and keeps only PNG or JPEG), and each needs a short description for people who cannot see it. The server cleans the HTML
 * again before saving, so what is typed here is never trusted as it is.
 */
export default function RichEditor({ label, value, onChange, upload, compact = false }: Props) {
  const box = useRef<HTMLDivElement>(null)
  const range = useRef<Range | null>(null)
  const fileInput = useRef<HTMLInputElement>(null)
  const [pending, setPending] = useState<File | null>(null)
  const [alt, setAlt] = useState('')
  const [selected, setSelected] = useState<HTMLImageElement | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<unknown>(null)

  useEffect(() => {
    if (box.current) box.current.innerHTML = value // set once: typing is not re-rendered, so the cursor never jumps
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const emit = () => onChange(box.current?.innerHTML ?? '')
  const remember = () => {
    const sel = window.getSelection()
    if (sel && sel.rangeCount > 0 && box.current?.contains(sel.anchorNode)) range.current = sel.getRangeAt(0).cloneRange()
  }
  const command = (name: string) => {
    box.current?.focus()
    document.execCommand?.(name)
    emit()
  }
  const take = (file: File | undefined) => {
    if (!file) return
    if (!/^image\/(png|jpeg)$/.test(file.type)) { setError(new ApiError(400, 'IMAGE_TYPE', 'Use a PNG or JPEG image')); return }
    setError(null); setAlt(''); setPending(file)
  }
  const insert = async () => {
    if (!pending || !alt.trim()) return
    setBusy(true); setError(null)
    try {
      const up = await upload(pending)
      const img = document.createElement('img')
      img.src = up.url; img.alt = alt.trim(); img.width = up.width > 600 ? 600 : up.width; img.height = Math.round((up.height * img.width) / up.width)
      const r = range.current
      if (r && box.current?.contains(r.commonAncestorContainer)) { r.collapse(false); r.insertNode(img) } else box.current?.appendChild(img)
      setPending(null); setAlt('')
      emit()
    } catch (e) {
      setError(e)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className={`rich-editor${compact ? ' compact' : ''}`}>
      <div className="re-bar" role="toolbar" aria-label={`${label}: formatting`}>
        {[['bold', 'B', 'Bold'], ['italic', 'I', 'Italic'], ['underline', 'U', 'Underline'], ['subscript', 'x₂', 'Subscript'], ['superscript', 'x²', 'Superscript'], ...(compact ? [] : [['insertUnorderedList', '• List', 'Bulleted list'], ['insertOrderedList', '1. List', 'Numbered list']])].map(([cmd, text, name]) => (
          <button key={cmd} type="button" title={name} aria-label={name} onMouseDown={(e) => e.preventDefault()} onClick={() => command(cmd)}>{text}</button>
        ))}
        <button type="button" onMouseDown={(e) => { remember(); e.preventDefault() }} onClick={() => fileInput.current?.click()}>Add image</button>
        <input ref={fileInput} type="file" accept="image/png,image/jpeg" hidden aria-label={`${label}: choose an image`} onChange={(e) => { take(e.target.files?.[0]); e.target.value = '' }} />
      </div>
      <div
        ref={box}
        className="re-box"
        contentEditable
        suppressContentEditableWarning
        role="textbox"
        aria-multiline="true"
        aria-label={label}
        onInput={emit}
        onBlur={() => { remember(); emit() }}
        onClick={(e) => setSelected(e.target instanceof HTMLImageElement ? e.target : null)}
        onPaste={(e) => {
          const file = [...e.clipboardData.files].find((f) => f.type.startsWith('image/'))
          e.preventDefault()
          if (file) { remember(); take(file); return }
          document.execCommand?.('insertText', false, e.clipboardData.getData('text/plain')) // plain text only: no formatting from Word or a web page
        }}
        onDrop={(e) => { const file = [...e.dataTransfer.files].find((f) => f.type.startsWith('image/')); if (file) { e.preventDefault(); remember(); take(file) } }}
      />
      {pending && (
        <div className="re-panel" role="group" aria-label="New image">
          <img className="re-thumb" src={URL.createObjectURL(pending)} alt="" />
          <label className="field" style={{ flex: 1 }}>Describe the image for someone who cannot see it (required)
            <input value={alt} onChange={(e) => setAlt(e.target.value)} placeholder="e.g. Series of five circles; the last is missing" maxLength={300} />
          </label>
          <div className="row">
            <button type="button" className="primary" disabled={!alt.trim() || busy} onClick={() => void insert()}>{busy ? 'Uploading…' : 'Insert image'}</button>
            <button type="button" disabled={busy} onClick={() => setPending(null)}>Cancel</button>
          </div>
        </div>
      )}
      {selected && !pending && (
        <div className="re-panel" role="group" aria-label="Selected image">
          <label className="field" style={{ flex: 1 }}>Image description
            <input value={selected.alt} onChange={(e) => { selected.alt = e.target.value; setSelected(selected); emit() }} maxLength={300} />
          </label>
          <div className="row">
            <button type="button" onClick={() => { selected.remove(); setSelected(null); emit() }}>Remove image</button>
            <button type="button" onClick={() => setSelected(null)}>Done</button>
          </div>
        </div>
      )}
      <ErrorNotice error={error} />
    </div>
  )
}
